import type { ReactNode } from "react";
import { Badge, cn, fmtDate, type Prescription } from "@dearbill/shared";

/** The same for every row while no program is stored per drug. */
const PROGRAM = "Medicare Prescription Payment Plan (M3P)";
const COLUMN_LABEL = "text-eyebrow-sm text-slate-teal uppercase";
/** Below `md` a cell is one stacked line of its row (label beside value); from `md` a table cell. */
const CELL = "flex items-center gap-3 px-5 py-1.5 last:pb-5 md:table-cell md:py-5";

interface MedicationsTableProps {
  prescriptions: Prescription[];
  /** The enrollment's `createdAt`, the date added of a row without `addedAt`; null without an enrollment. */
  createdAt: string | null;
}

/**
 * The prescription table. From `md` it is a four-column `<table>` under an
 * Ice header band; below `md` the header is hidden and each row stacks its
 * cells, the Status, Date added and Support program cells carrying their
 * column label inline, so nothing scrolls sideways at 390px. Status and
 * Support program read the same for every row while neither is stored per
 * drug. An empty list is one row spanning the table.
 */
export function MedicationsTable({ prescriptions, createdAt }: MedicationsTableProps): ReactNode {
  return (
    <div className="border-haze overflow-hidden rounded-2xl border bg-white">
      <table className="block w-full text-left md:table">
        <thead className="bg-ice hidden md:table-header-group">
          <tr className={cn("border-haze border-b", COLUMN_LABEL)}>
            <th scope="col" className="p-5">
              Medication & dosage
            </th>
            <th scope="col" className="w-45 p-5">
              Status
            </th>
            <th scope="col" className="w-40 p-5">
              Date added
            </th>
            <th scope="col" className="w-70 p-5">
              Support program
            </th>
          </tr>
        </thead>
        <tbody className="block md:table-row-group">
          {prescriptions.length === 0 ? (
            <tr>
              <td colSpan={4} className="text-body-sm text-slate-teal p-5">
                {"Your medications will appear here once they're added to your enrollment."}
              </td>
            </tr>
          ) : (
            prescriptions.map((p) => (
              <MedicationRow key={p.rxcui} prescription={p} createdAt={createdAt} />
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function MedicationRow({
  prescription: p,
  createdAt,
}: {
  prescription: Prescription;
  createdAt: string | null;
}): ReactNode {
  const subline = p.label.trim() || p.form.trim();
  return (
    <tr className="border-haze block border-b last:border-b-0 md:table-row">
      <td className="block px-5 pb-1.5 pt-5 md:table-cell md:py-5">
        <p className="text-body text-midnight font-bold">{p.drugName}</p>
        {subline !== "" && <p className="text-body-sm text-slate-teal">{subline}</p>}
      </td>
      <td className={CELL}>
        <span className={cn(COLUMN_LABEL, "md:hidden")}>Status</span>
        <Badge tone="active">Active</Badge>
      </td>
      <td className={cn(CELL, "text-body-sm text-slate-teal")}>
        <span className={cn(COLUMN_LABEL, "md:hidden")}>Date added</span>
        {fmtDate(p.addedAt ?? createdAt)}
      </td>
      <td className={cn(CELL, "text-body-sm text-midnight font-semibold")}>
        <span className={cn(COLUMN_LABEL, "md:hidden")}>Support program</span>
        {PROGRAM}
      </td>
    </tr>
  );
}
